<template>
  <div class="shortcuts-buttons">
    <button class="btn btn-blue-outline btn-rounded" @click="toggleBreakAuction">
      <base-icon v-if="liveBreakEnd" icon-name="play" width="29" height="37" view-box="0 0 29 36.908">
        <play-icon></play-icon>
      </base-icon>
      <base-icon v-else icon-name="pause" width="31" height="36" view-box="0 0 30.721 35.841">
        <pause-icon></pause-icon>
      </base-icon>
      <span class="label">{{ breakButtonLabel }}</span>
    </button>

    <button
      class="btn btn-blue-outline btn-rounded"
      :disabled="waitServer.auctionType"
      @click="toggleAutoAuction">
      <base-icon v-if="isAutomatedLive" icon-name="manual" width="38" height="38" view-box="0 0 37.718 37.718">
        <manual-icon></manual-icon>
      </base-icon>
      <base-icon v-else icon-name="auto" width="38" height="38" view-box="0 0 38.269 38.269">
        <auto-icon></auto-icon>
      </base-icon>
      <span class="label">{{ auctionTypeLabel }}</span>
    </button>

    <button class="btn btn-blue-outline btn-rounded" @click="toggleLockButtons">
      <base-icon
        v-if="!auctionStore.isLockedButtons"
        icon-name="lock"
        width="29"
        height="38"
        view-box="0 0 17.721 23.259">
        <lock-icon></lock-icon>
      </base-icon>
      <base-icon v-else icon-name="unlock" width="29" height="38" view-box="0 0 18 24">
        <unlock-icon></unlock-icon>
      </base-icon>
      <span class="label">{{ blockButtonsText }}</span>
    </button>

    <button
      class="btn btn-blue-outline btn-rounded"
      :disabled="waitServer.bookBidFirst"
      @click="bookBidFirst">
      <base-icon icon-name="book-bid" width="37" height="38" view-box="0 0 36.663 38.013">
        <bid-hammer-icon></bid-hammer-icon>
      </base-icon>
      <span class="label">{{ trans("live.common.shortcuts.book_bid_first") }}</span>
    </button>

    <button
      class="btn btn-blue-outline btn-rounded"
      @click="changePrice"
      :disabled="!auctionStore.activeLot?.id">
      <base-icon icon-name="currency" width="21" height="38" view-box="0 0 21.499 38.013">
        <currency-icon></currency-icon>
      </base-icon>
      <span class="label">{{ trans("live.common.shortcuts.change_price") }}</span>
    </button>

    <button
      :disabled="!activeLot?.leaders_list?.length || waitServer.retractBid"
      class="btn btn-blue-outline btn-rounded"
      @click="retractBid">
      <base-icon icon-name="undo" width="41" height="18" view-box="0 0 41.272 18.145">
        <undo-icon></undo-icon>
      </base-icon>
      <span class="label">{{ trans("live.common.shortcuts.retract_bid") }}</span>
    </button>
  </div>
  <popup class="instant-break-popup" title="Make an Instant Break" ref="popup"></popup>
  <popup
    class="change-price-popup"
    :z-index="14"
    :title="trans('live.modals.change_price.title')"
    ref="changePricePopup"></popup>
</template>

<script setup lang="ts">
  import {
    BaseIcon,
    BidHammerIcon,
    AutoIcon,
    CurrencyIcon,
    LockIcon,
    PauseIcon,
    PlayIcon,
    UndoIcon,
    UnlockIcon,
    ManualIcon
  } from "@/components/icons";
  import Api from "@/services/api";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { computed, inject, markRaw, nextTick, reactive, ref } from "vue";
  import { trans } from "@/utils/trans";
  import Popup from "@/components/_partials/Popup.vue";
  import InstantBreakPopup from "@/components/_partials/InstantBreakPopup.vue";
  import ChangePricePopup from "@/components/_partials/ChangePricePopup.vue";
  import { useToast } from "primevue/usetoast";

  const auctionStore = useAuctionStore();
  const auction = computed(() => auctionStore.auction);
  const activeLot = computed(() => auctionStore.activeLot);

  const emitter = inject("emitter");
  const toast = useToast();

  const popup = ref<typeof Popup>();
  const changePricePopup = ref<typeof Popup>();
  const breakComponent = markRaw(InstantBreakPopup);
  const changePriceComponent = markRaw(ChangePricePopup);

  const emit = defineEmits(["closeForm", "changeTitle", "setModalHeightAuto"]);

  const waitServer = reactive({
    break: false,
    auctionType: false,
    bookBidFirst: false,
    retractBid: false
  });

  const liveBreakEnd = computed(() => auction.value.live_break_end);
  const breakButtonLabel = computed(() => {
    return trans("live.common.shortcuts." + (liveBreakEnd.value ? "resume_auction" : "instant_break"));
  });

  const isAutomatedLive = computed(() => {
    return (
      auctionStore.liveSettings?.is_open_next_lot_auto &&
      auctionStore.liveSettings?.is_change_bidding_status_auto &&
      auctionStore.liveSettings?.is_complete_current_lot_auto
    );
  });
  const auctionTypeLabel = computed(() => {
    return trans("live.common.shortcuts." + (!isAutomatedLive.value ? "run_auto" : "run_manual"));
  });

  const blockButtonsText = computed(() => {
    return trans("live.common.shortcuts." + (!auctionStore.isLockedButtons ? "block_buttons" : "unlock_buttons"));
  });

  const toggleBreakAuction = () => {
    auctionStore.setBreak();
    // if (!liveBreakEnd.value) {
    //   popup.value?.toggleComponent(breakComponent);
    //   nextTick(() => {
    //     popup.value?.open();
    //   });
    // }
  };

  const toggleAutoAuction = () => {
    if (waitServer.auctionType) return;
    waitServer.auctionType = true;

    const settings = isAutomatedLive.value
      ? { is_open_next_lot_auto: false, is_change_bidding_status_auto: false, is_complete_current_lot_auto: false }
      : { is_open_next_lot_auto: true, is_change_bidding_status_auto: true, is_complete_current_lot_auto: true };

    Api.put(auctionStore.routes["auctions.update_live_settings"], settings)
      .then((response) => {
        if (response.data.status && response.data.settings) {
          auctionStore.$patch(response.data.settings);
        } else {
          console.log(response);
        }
      })
      .finally(() => {
        waitServer.auctionType = false;
      });
  };

  const toggleLockButtons = () => {
    auctionStore.$patch({
      isLockedButtons: !auctionStore.isLockedButtons
    });
  };

  const bookBidFirst = () => {
    alert("In development");
    emit("closeForm");
  };

  const changePrice = () => {
    if (auctionStore.activeLot?.id) {
      auctionStore.activeLot.bid_increment_rules = auctionStore.auction.bid_increment_rules;
      auctionStore.activeLot.bid_variants = auctionStore.auction.bid_variants;
      auctionStore.activeLot.bid_infinite_step = auctionStore.auction.bid_infinite_step;
      auctionStore.activeLot.bid_on_starting_price = auctionStore.auction.bid_on_starting_price;
      changePricePopup.value?.toggleComponent(changePriceComponent);

      nextTick(() => {
        changePricePopup.value?.open();
      });
    } else {
      toast.add({
        severity: "error",
        summary: trans("live.toast.error"),
        detail: trans("No active lot"),
        life: 6000
      });
    }
  };

  const retractBid = () => {
    emitter?.emit("shortcuts:retract-bid");
    emit("closeForm");
  };
</script>
